import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { BrowserRouter, Navigate, Route, Routes, useParams, useNavigate } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { App } from './App';
import { PublicFeedbackFormPage } from 'pages/PublicFeedbackFormPage';
import { LandingPage } from 'pages/LandingPage';
import { PageTransition } from './components/PageTransition';
import { AuthProvider } from './context/AuthContext';
import { ShareableHealthActionPage } from 'pages/ShareableHealthActionPage';
import { getLanguageFromBrowser } from './utils/language';

type ConsoleLogger = (...args: unknown[]) => void;

const eirConsoleMarkdown = `
# EIR.SPACE
> Personal AI Health Assistant

\`\`\`
 _____ ___ ____    ____  ____   _    ____ _____ 
| ____|_ _|  _ \\  / ___||  _ \\ / \\  / ___| ____|
|  _|  | || |_) | \\___ \\| |_) / _ \\| |   |  _|  
| |___ | ||  _ < _ ___) |  __/ ___ \\ |___| |___ 
|_____|___|_| \\_(_)____/|_| /_/   \\_\\____|_____|
\`\`\`

[**Join us on Discord →**](https://discord.gg/Rd3nrPqpBv)
`;

const displayEirConsoleBanner = (logger: ConsoleLogger): void => {
  logger(eirConsoleMarkdown);
};

const keepVerboseConsole =
  import.meta.env.VITE_CHAT_DEBUG === 'true' || import.meta.env.VITE_DEBUG === 'true';

if (import.meta.env.PROD && !keepVerboseConsole) {
  const originalLog = console.log.bind(console);

  displayEirConsoleBanner(originalLog);

  const noop = (..._args: unknown[]): void => undefined;

  console.log = noop;
  console.info = noop;
  console.debug = noop;
} else {
  displayEirConsoleBanner(console.log.bind(console));
}

const AppShell: React.FC = () => (
  <AuthProvider>
    <AppProvider>
      <App />
    </AppProvider>
  </AuthProvider>
);

const PublicFeedbackFormRoute: React.FC = () => {
  const { externalId } = useParams<{ externalId: string }>();

  if (!externalId) {
    return <Navigate to="/" replace />;
  }

  return <PublicFeedbackFormPage externalId={externalId} />;
};

const ShareableHealthActionRoute: React.FC = () => {
  const { actionId } = useParams<{ actionId: string }>();

  if (!actionId) {
    return <Navigate to="/" replace />;
  }

  return <ShareableHealthActionPage actionId={decodeURIComponent(actionId)} />;
};

const LanguageRedirect: React.FC<{ redirectTo?: string }> = ({ redirectTo }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const lang = getLanguageFromBrowser();
    const targetPath = redirectTo || `/${lang}/start`;
    navigate(targetPath, { replace: true });
  }, [navigate, redirectTo]);

  return null;
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <PageTransition>
      <Routes>
        <Route path="/sv/start" element={<LandingPage language="sv" />} />
        <Route path="/en/start" element={<LandingPage language="en" />} />
        <Route path="/start" element={<LanguageRedirect />} />
        <Route path="/feedback/form/:externalId" element={<PublicFeedbackFormRoute />} />
        <Route path="/health-action/:actionId" element={<ShareableHealthActionRoute />} />
        <Route path="/" element={<AppShell />} />
        <Route path="/*" element={<AppShell />} />
      </Routes>
      </PageTransition>
    </BrowserRouter>
  </React.StrictMode>
);
